import React, { useState } from 'react';
import { groupBy, first, isArray, isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { computeProductOptionLabel } from 'src/scripts/templates/product/utils';
import { CartData, CartItem, changeItemQuantity, OptionsWithValue, ShopifyPrice } from 'src/scripts/utils/cart';
import { lowerCasedBlacklistedOptionValues } from 'src/scripts/utils/constants';
import { toLocalCurrencyShowEstimate } from 'src/scripts/utils/currency';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { PurchaseLimitTagDataTypes } from 'src/scripts/templates/_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import { checkIfItemCanBeSoldOut } from 'src/scripts/utils/helpers';
import { VARIANT_URGENCY_QUANTITY_CRITERIA } from 'src/scripts/templates/product/components/ScarcityMessaging/constants';
import { destructureFitAndInseam } from 'src/scripts/templates/product/components/LengthToggle';

export const computeCartItemGroups = (cartData: CartData) => {
  const { regularItems } = cartData;
  const { undefined: nonSetCartItems = [], ...setCartItems } = groupBy(regularItems, 'properties._setUid'); // preseve original sort order of items after seperating sets & non-sets

  const originalSortOrder = regularItems.reduce((hash: { [key: string]: number }, item, index) => {
    hash[item.key] = index;
    return hash;
  }, {});
  const cartItemGroupsToRender = [...nonSetCartItems, ...Object.values(setCartItems)].sort((a, b) => {
    const aKey = isArray(a)
      ? (
          first(a) || {
            key: '',
          }
        ).key
      : a.key;
    const bKey = isArray(b)
      ? (
          first(b) || {
            key: '',
          }
        ).key
      : b.key; //@ts-expect-error // ? [REVISIT]

    return originalSortOrder[aKey] - originalSortOrder[bKey];
  });
  return cartItemGroupsToRender;
};

export const getStrikeThroughLinePrice = (item: CartItem) => {
  const { original_line_price, price, displayPrice = price, quantity } = item;
  if (original_line_price > displayPrice * quantity) {
    return original_line_price;
  } else if (displayPrice && displayPrice > price) {
    return displayPrice * quantity;
  } else {
    return null;
  }
};

export const formatShopifyPrice = (shopifyPrice: ShopifyPrice) => toLocalCurrencyShowEstimate(shopifyPrice / 100);

export const computeDisplayForOptionWithValues = ({
  options,
  productType,
  tags,
}: {
  options: OptionsWithValue[];
  productType?: string;
  tags?: string[];
}) => {
  return options
    .map(({ name, value }, index) => {
      const isValid = !lowerCasedBlacklistedOptionValues.includes(value.toLowerCase().replace(' ', ''));
      const label = computeProductOptionLabel({
        optionName: name,
        optionNumber: index + 1,
        productType,
        tags,
      });
      if (label === 'Length') {
        const { inseam } = destructureFitAndInseam(value);
        if (inseam) {
          value = `${inseam} Inseam`;
        }
      }
      return isValid
        ? {
            label,
            value,
          }
        : null;
    })
    .filter(notNil);
};

// what is the observable here?
export const QuantityChangeButtons = observer(
  ({
    buttonClassName = '',
    isUnlimited,
    itemKey,
    quantity,
    wrapperClassName = '',
    purchaseLimitTagData,
    quantityAvailable,
  }: {
    buttonClassName?: string;
    isUnlimited: boolean;
    itemKey: string;
    quantity: number;
    wrapperClassName?: string;
    quantityAvailable: number | null | undefined;
    purchaseLimitTagData: PurchaseLimitTagDataTypes | undefined;
  }) => {
    const [isLoading, setIsLoading] = useState(false);

    const quantityLimitReached =
      ((quantityAvailable && quantityAvailable === quantity) ||
        (purchaseLimitTagData && purchaseLimitTagData?.purchaseLimitNumber === quantity)) ??
      false;

    const incrementDisabled = !isNil(quantityAvailable) && (quantityLimitReached || quantityAvailable === 0);

    const onQuantityIncrement = async () => {
      // this is a bit weird, we don't check for `isUnlimited` earlier for `incrementDisabled` but we do here
      // also the check here seems redundant since we already check for `incrementDisabled` above
      // however kept this mostly the same as the original code to limit the changes
      if (isUnlimited || !incrementDisabled) {
        setIsLoading(true);
        await changeItemQuantity(itemKey, quantity + 1, true);
        setIsLoading(false);
      }
    };

    const onQuantityDecrement = async () => {
      setIsLoading(true);
      await changeItemQuantity(itemKey, quantity < 1 ? 0 : quantity - 1);
      setIsLoading(false);
    };

    return (
      <span className={`quantity__wrapper ${wrapperClassName}`}>
        <button
          className={`button__minus--new ${buttonClassName}`}
          type="button"
          aria-label="Decrement quantity"
          onClick={onQuantityDecrement}
          disabled={isLoading}
        >
          <span className={`icon__minus--new icon__increment`} />
        </button>
        <span className="item__quantity">{quantity}</span>
        <button
          className={`button__plus--new ${buttonClassName} ${incrementDisabled && `disabled`}`}
          type="button"
          aria-label="Increment quantity"
          onClick={onQuantityIncrement}
          disabled={incrementDisabled || isLoading}
          style={{ cursor: incrementDisabled ? 'not-allowed' : undefined }}
        >
          <span className={`icon__plus--new icon__increment`} />
        </button>
      </span>
    );
  }
);

export const getAlmostGoneValue = (tags: string[] = [], quantityAvailable: number | null) => {
  if (tags.length === 0 || quantityAvailable === null) return { isAlmostGone: false };
  const isAlmostGone = checkIfItemCanBeSoldOut(tags) ? quantityAvailable <= VARIANT_URGENCY_QUANTITY_CRITERIA : false;
  return { isAlmostGone };
};
